import {
  areEoiAndUpdatedValuesEqual,
  EOI_UPDATABLE_PARAMETER_KEYS,
  formatParameterValueDisplay,
  isEoiParameterUpdateComplete,
  listFinalMilestoneParameterDefinitions,
  normalizeCatalogParameterUpdate,
} from "./catalogEoiParameters";
import { MILESTONE_STATUS } from "./constants";
import {
  getRemanufacturingActivity,
  getRemanufacturingActivityCategory,
  REMANUFACTURING_PROGRESS_TYPE,
} from "./remanufacturingActivityCatalog";

/** Sequential catalog milestone keys: **SVC** = service (catalog) milestone. */
export const CATALOG_MILESTONE_KEY_PREFIX = "SVC";

const CATALOG_MILESTONE_KEY_RE = /^SVC(\d+)$/i;

/**
 * @typedef {Object} CatalogMilestoneActivityProgress
 * @property {string} activityId
 * @property {boolean} [completed]
 * @property {number} [progressPercent] Working value while editing; may be coerced to 100 for API on milestone complete.
 * @property {number} [recordedProgressPercent] Provider-entered % preserved for dashboard display.
 */

/**
 * @typedef {import("./catalogEoiParameters").CatalogMilestoneParameterUpdate} CatalogMilestoneParameterUpdate
 */

/**
 * @typedef {Object} CatalogMilestoneInputs
 * @property {CatalogMilestoneActivityProgress[]} activities
 * @property {CatalogMilestoneParameterUpdate[]} [parameterUpdates]
 * @property {Array<{ assetRef: string, fileName?: string, mimeType?: string }>} outcomeFiles
 * @property {number} [progressPercent]
 * @property {boolean} [serviceCompletionConfirmed]
 * @property {string} [serviceDppPreviewGeneratedAt]
 * @property {string} [dppRegistryUid]
 * @property {Record<string, string>} [dppSealedDocuments]
 * @property {string[]} [dppMismatchFieldIds]
 */

/**
 * @typedef {Object} CatalogServiceMilestone
 * @property {string} id
 * @property {string} key
 * @property {string} title
 * @property {import("./constants").MilestoneStatus} status
 * @property {number} sortOrder
 * @property {CatalogMilestoneInputs} inputs
 */

/**
 * @param {unknown} key
 */
export function isCatalogMilestoneKey(key) {
  return CATALOG_MILESTONE_KEY_RE.test(String(key ?? ""));
}

/**
 * User-facing label for catalog milestone keys (SVC1 → M1).
 * Internal API keys remain SVC{n}.
 *
 * @param {unknown} key
 */
export function formatCatalogMilestoneDisplayKey(key) {
  const match = CATALOG_MILESTONE_KEY_RE.exec(String(key ?? ""));
  if (!match) return String(key ?? "");
  return `M${match[1]}`;
}

/**
 * @param {unknown} raw
 * @returns {import("./constants").MilestoneStatus}
 */
export function normalizeCatalogMilestoneStatus(raw) {
  const normalized = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    normalized === MILESTONE_STATUS.IN_PROGRESS ||
    normalized === "inprogress"
  ) {
    return MILESTONE_STATUS.IN_PROGRESS;
  }

  if (
    normalized === MILESTONE_STATUS.COMPLETED ||
    normalized === "complete" ||
    normalized === "done"
  ) {
    return MILESTONE_STATUS.COMPLETED;
  }

  if (normalized === MILESTONE_STATUS.PENDING) {
    return MILESTONE_STATUS.PENDING;
  }

  return MILESTONE_STATUS.PENDING;
}

/**
 * True when a persisted API milestone row is missing progress the client just saved.
 *
 * @param {CatalogServiceMilestone} persisted
 * @param {CatalogServiceMilestone} sent
 */
/**
 * @param {unknown[]|null|undefined} persistedMilestones
 * @param {unknown[]|null|undefined} sentMilestones
 */
export function catalogMilestoneListsNeedReconcile(
  persistedMilestones,
  sentMilestones
) {
  const sent = normalizeCatalogMilestonesList(sentMilestones);
  const persisted = normalizeCatalogMilestonesList(persistedMilestones);
  return catalogMilestoneListsNeedReconcileNormalized(persisted, sent);
}

/**
 * @param {CatalogServiceMilestone[]} persisted
 * @param {CatalogServiceMilestone[]} sent
 */
function catalogMilestoneListsNeedReconcileNormalized(persisted, sent) {
  if (!sent.length) {
    return false;
  }

  if (
    sent.some((milestone) => milestone.status === MILESTONE_STATUS.COMPLETED)
  ) {
    return true;
  }

  return sent.some((sentMilestone) => {
    const persistedMilestone = persisted.find(
      (milestone) => milestone.key === sentMilestone.key
    );
    if (!persistedMilestone) {
      return true;
    }
    return catalogMilestoneSnapshotIsStale(persistedMilestone, sentMilestone);
  });
}

/**
 * Prefer the validated client milestone list when the API snapshot is incomplete
 * (common on staging backends that omit status or activity progress on PUT).
 *
 * @param {unknown[]|null|undefined} persistedMilestones
 * @param {unknown[]|null|undefined} sentMilestones
 * @returns {CatalogServiceMilestone[]}
 */
export function reconcileCatalogMilestoneLists(
  persistedMilestones,
  sentMilestones
) {
  const sent = normalizeCatalogMilestonesList(sentMilestones);
  if (!sent.length) {
    return normalizeCatalogMilestonesList(persistedMilestones);
  }

  const persisted = normalizeCatalogMilestonesList(persistedMilestones);
  if (!catalogMilestoneListsNeedReconcileNormalized(persisted, sent)) {
    return persisted;
  }

  const sentKeys = new Set(sent.map((milestone) => milestone.key));
  return [
    ...sent,
    ...persisted.filter((milestone) => !sentKeys.has(milestone.key)),
  ].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function catalogMilestoneSnapshotIsStale(persisted, sent) {
  if (
    sent.status === MILESTONE_STATUS.COMPLETED &&
    persisted.status !== MILESTONE_STATUS.COMPLETED
  ) {
    return true;
  }

  if (
    sent.status === MILESTONE_STATUS.IN_PROGRESS &&
    persisted.status === MILESTONE_STATUS.PENDING
  ) {
    return true;
  }

  for (const sentActivity of sent.inputs?.activities ?? []) {
    const persistedActivity = (persisted.inputs?.activities ?? []).find(
      (row) => row.activityId === sentActivity.activityId
    );
    if (!persistedActivity) {
      return true;
    }

    const catalogActivity = getRemanufacturingActivity(sentActivity.activityId);
    if (
      catalogActivity?.progressType ===
      REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
    ) {
      const sentPercent = getCatalogActivityRecordedPercent(sentActivity);
      const persistedPercent =
        getCatalogActivityRecordedPercent(persistedActivity);
      if (sentPercent !== persistedPercent) {
        return true;
      }
      continue;
    }

    if (sentActivity.completed && !persistedActivity.completed) {
      return true;
    }
  }

  return false;
}

/**
 * Resolves the latest recorded progress for an activity when rendering milestone summaries.
 *
 * @param {CatalogServiceMilestone[]} milestones
 * @param {CatalogMilestoneActivityProgress} activityProgress
 */
export function resolveCatalogActivityProgressForDisplay(
  milestones,
  activityProgress
) {
  const latest = getLatestCatalogActivityProgress(
    milestones,
    activityProgress.activityId
  );
  return latest ?? activityProgress;
}

/**
 * @param {unknown} value
 */
function clampCatalogPercent(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? Math.min(100, Math.max(0, Math.round(numeric)))
    : 0;
}

/**
 * @param {CatalogMilestoneActivityProgress} activityProgress
 */
export function getCatalogActivityRecordedPercent(activityProgress) {
  const recorded = Number(activityProgress?.recordedProgressPercent);
  if (Number.isFinite(recorded)) {
    return clampCatalogPercent(recorded);
  }
  return clampCatalogPercent(activityProgress?.progressPercent ?? 0);
}

/**
 * Milestone row status for dashboard summaries (treats preview/completed markers as done).
 *
 * @param {CatalogServiceMilestone} milestone
 */
export function resolveCatalogMilestoneDisplayStatus(milestone) {
  if (milestone.status === MILESTONE_STATUS.COMPLETED) {
    return MILESTONE_STATUS.COMPLETED;
  }
  if (milestone.inputs?.serviceDppPreviewGeneratedAt) {
    return MILESTONE_STATUS.COMPLETED;
  }
  return milestone.status;
}

/**
 * @param {CatalogServiceMilestone} milestone
 * @param {CatalogMilestoneActivityProgress} activityProgress
 */
export function getCatalogActivityDisplayPercent(_milestone, activityProgress) {
  return getCatalogActivityRecordedPercent(activityProgress);
}

/**
 * @param {CatalogServiceMilestone} milestone
 * @param {CatalogMilestoneActivityProgress} activityProgress
 */
export function getCatalogActivityDisplayDone(milestone, activityProgress) {
  if (
    resolveCatalogMilestoneDisplayStatus(milestone) ===
    MILESTONE_STATUS.COMPLETED
  ) {
    return true;
  }
  return Boolean(activityProgress.completed);
}

/**
 * Captures provider-entered values before we coerce fields for legacy API validators.
 *
 * @param {CatalogServiceMilestone} milestone
 */
export function stampCatalogMilestoneRecordedProgress(milestone) {
  return {
    ...milestone,
    inputs: {
      ...milestone.inputs,
      activities: (milestone.inputs?.activities ?? []).map((row) => {
        const catalogActivity = getRemanufacturingActivity(row.activityId);
        if (
          catalogActivity?.progressType ===
          REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
        ) {
          const recorded = getCatalogActivityRecordedPercent(row);
          return {
            ...row,
            recordedProgressPercent: recorded,
            progressPercent: recorded,
          };
        }
        if (
          catalogActivity?.progressType ===
          REMANUFACTURING_PROGRESS_TYPE.DONE_TICK
        ) {
          return { ...row, completed: true };
        }
        return row;
      }),
      parameterUpdates: (milestone.inputs?.parameterUpdates ?? []).map(
        (row) => ({
          ...row,
          completed: isEoiParameterUpdateComplete(row),
        })
      ),
    },
  };
}

/**
 * API payload activities for PUT (legacy backends may require 100% / completed on milestone complete).
 *
 * @param {CatalogServiceMilestone} milestone
 */
/**
 * @param {CatalogServiceMilestone} milestone
 */
export function buildCatalogMilestoneParameterUpdatesForApi(milestone) {
  const normalized = normalizeCatalogMilestone(milestone);
  const milestoneComplete =
    normalized.status === MILESTONE_STATUS.COMPLETED ||
    Boolean(normalized.inputs?.serviceDppPreviewGeneratedAt);

  return (normalized.inputs.parameterUpdates ?? []).map((row) => ({
    parameterKey: row.parameterKey,
    ...(row.value ? { value: row.value } : {}),
    ...(row.unit ? { unit: row.unit } : {}),
    completed: milestoneComplete
      ? isEoiParameterUpdateComplete(row)
      : Boolean(row.completed),
  }));
}

/**
 * @param {CatalogServiceMilestone} milestone
 */
export function buildCatalogMilestoneActivitiesForApi(milestone) {
  const normalized = normalizeCatalogMilestone(milestone);
  const milestoneComplete =
    normalized.status === MILESTONE_STATUS.COMPLETED ||
    Boolean(normalized.inputs?.serviceDppPreviewGeneratedAt);

  return normalized.inputs.activities.map((activityProgress) => {
    const catalogActivity = getRemanufacturingActivity(
      activityProgress.activityId
    );
    if (
      catalogActivity?.progressType ===
      REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
    ) {
      const recorded = getCatalogActivityRecordedPercent(activityProgress);
      return {
        activityId: activityProgress.activityId,
        progressPercent: milestoneComplete ? 100 : recorded,
        recordedProgressPercent: recorded,
      };
    }

    return {
      activityId: activityProgress.activityId,
      completed: milestoneComplete ? true : Boolean(activityProgress.completed),
    };
  });
}

/**
 * @param {Array<{ key?: string }>} milestones
 */
export function nextCatalogMilestoneKey(milestones) {
  const numbers = milestones
    .map((milestone) => {
      const match = CATALOG_MILESTONE_KEY_RE.exec(String(milestone?.key ?? ""));
      return match ? Number(match[1]) : 0;
    })
    .filter((value) => Number.isFinite(value));

  const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  return `${CATALOG_MILESTONE_KEY_PREFIX}${next}`;
}

/**
 * @param {unknown} raw
 * @returns {CatalogMilestoneActivityProgress}
 */
function normalizeCatalogActivityProgress(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { activityId: "" };
  }

  const record = /** @type {Record<string, unknown>} */ (raw);
  const activityId = String(
    record.activityId ?? record.activity_id ?? ""
  ).trim();
  const catalogActivity = getRemanufacturingActivity(activityId);

  if (
    catalogActivity?.progressType ===
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
  ) {
    const progressPercent = Number(
      record.progressPercent ?? record.progress_percent
    );
    const recordedRaw = Number(
      record.recordedProgressPercent ?? record.recorded_progress_percent
    );
    const clampedProgress = Number.isFinite(progressPercent)
      ? clampCatalogPercent(progressPercent)
      : 0;
    const clampedRecorded = Number.isFinite(recordedRaw)
      ? clampCatalogPercent(recordedRaw)
      : clampedProgress < 100 && clampedProgress > 0
        ? clampedProgress
        : undefined;

    return {
      activityId,
      progressPercent: clampedProgress,
      ...(clampedRecorded != null
        ? { recordedProgressPercent: clampedRecorded }
        : {}),
    };
  }

  return {
    activityId,
    completed: Boolean(record.completed ?? record.done),
  };
}

/**
 * @param {unknown} raw
 */
function normalizeOutcomeFile(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { assetRef: "" };
  }
  const record = /** @type {Record<string, unknown>} */ (raw);
  return {
    assetRef: String(record.assetRef ?? record.asset_ref ?? ""),
    ...(typeof record.fileName === "string"
      ? { fileName: record.fileName }
      : typeof record.file_name === "string"
        ? { fileName: record.file_name }
        : {}),
    ...(typeof record.mimeType === "string"
      ? { mimeType: record.mimeType }
      : typeof record.mime_type === "string"
        ? { mimeType: record.mime_type }
        : {}),
  };
}

/**
 * @param {unknown} raw
 * @returns {CatalogMilestoneInputs}
 */
export function normalizeCatalogMilestoneInputs(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { activities: [], outcomeFiles: [] };
  }

  const record = /** @type {Record<string, unknown>} */ (raw);
  const activitiesRaw = record.activities;
  const outcomeFilesRaw = record.outcomeFiles ?? record.outcome_files;

  const activities = Array.isArray(activitiesRaw)
    ? activitiesRaw
        .map(normalizeCatalogActivityProgress)
        .filter((row) => row.activityId)
    : [];

  const parameterUpdatesRaw =
    record.parameterUpdates ?? record.parameter_updates;
  const parameterUpdates = Array.isArray(parameterUpdatesRaw)
    ? parameterUpdatesRaw
        .map(normalizeCatalogParameterUpdate)
        .filter((row) => row.parameterKey)
    : [];

  const outcomeFiles = Array.isArray(outcomeFilesRaw)
    ? outcomeFilesRaw.map(normalizeOutcomeFile).filter((file) => file.assetRef)
    : [];

  const progressPercent = Number(
    record.progressPercent ?? record.progress_percent
  );
  const serviceCompletionConfirmed = Boolean(
    record.serviceCompletionConfirmed ?? record.service_completion_confirmed
  );
  const dppRegistryUid = normalizeDppRegistryUid(
    record.dppRegistryUid ?? record.dpp_registry_uid
  );
  const dppSealedDocuments = normalizeDppSealedDocuments(
    record.dppSealedDocuments ?? record.dpp_sealed_documents
  );
  const dppMismatchFieldIds = normalizeDppMismatchFieldIds(
    record.dppMismatchFieldIds ?? record.dpp_mismatch_field_ids
  );

  return {
    activities,
    ...(parameterUpdates.length > 0 ? { parameterUpdates } : {}),
    outcomeFiles,
    ...(Number.isFinite(progressPercent)
      ? {
          progressPercent: Math.min(
            100,
            Math.max(0, Math.round(progressPercent))
          ),
        }
      : {}),
    ...(serviceCompletionConfirmed ? { serviceCompletionConfirmed: true } : {}),
    ...(typeof record.serviceDppPreviewGeneratedAt === "string"
      ? { serviceDppPreviewGeneratedAt: record.serviceDppPreviewGeneratedAt }
      : typeof record.service_dpp_preview_generated_at === "string"
        ? {
            serviceDppPreviewGeneratedAt:
              record.service_dpp_preview_generated_at,
          }
        : {}),
    ...(dppRegistryUid ? { dppRegistryUid } : {}),
    ...(dppSealedDocuments ? { dppSealedDocuments } : {}),
    ...(dppMismatchFieldIds ? { dppMismatchFieldIds } : {}),
  };
}

/**
 * @param {unknown} raw
 * @returns {string|null}
 */
function normalizeDppRegistryUid(raw) {
  if (typeof raw !== "string") return null;
  const uid = raw.trim();
  return uid === "" ? null : uid;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, string>|null}
 */
function normalizeDppSealedDocuments(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  /** @type {Record<string, string>} */
  const sealed = {};
  for (const [fieldId, assetRef] of Object.entries(raw)) {
    if (!fieldId || typeof assetRef !== "string" || assetRef.trim() === "") {
      continue;
    }
    sealed[fieldId] = assetRef;
  }
  return Object.keys(sealed).length > 0 ? sealed : null;
}

/**
 * @param {unknown} raw
 * @returns {string[]|null}
 */
function normalizeDppMismatchFieldIds(raw) {
  /** @type {unknown} */
  let values = raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      values = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(values)) return null;
  const ids = values.filter((id) => typeof id === "string" && id.trim() !== "");
  return ids.length > 0 ? ids : null;
}

/**
 * @param {unknown} raw
 * @returns {CatalogServiceMilestone}
 */
export function normalizeCatalogMilestone(raw) {
  const record =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? /** @type {Record<string, unknown>} */ (raw)
      : {};

  const key = String(record.key ?? "");
  const sortOrder = Number(record.sortOrder ?? record.sort_order);

  return {
    id: String(record.id ?? (key.toLowerCase() || "svc-milestone")),
    key,
    title: String(record.title ?? "").trim() || key,
    status: normalizeCatalogMilestoneStatus(record.status),
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : 1,
    inputs: normalizeCatalogMilestoneInputs(record.inputs),
  };
}

/**
 * @param {unknown[]|null|undefined} milestones
 * @returns {CatalogServiceMilestone[]}
 */
export function normalizeCatalogMilestonesList(milestones) {
  if (!Array.isArray(milestones)) return [];
  return milestones
    .filter((row) =>
      isCatalogMilestoneKey(/** @type {{ key?: string }} */ (row)?.key)
    )
    .map(normalizeCatalogMilestone)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Latest recorded progress for an activity across earlier milestones (by sort order).
 *
 * @param {CatalogServiceMilestone[]} milestones
 * @param {string} activityId
 * @returns {CatalogMilestoneActivityProgress|null}
 */
export function getLatestCatalogActivityProgress(milestones, activityId) {
  let latest = null;

  for (const milestone of normalizeCatalogMilestonesList(milestones)) {
    const entry = milestone.inputs.activities.find(
      (row) => row.activityId === activityId
    );
    if (entry) {
      latest = entry;
    }
  }

  return latest;
}

/**
 * @param {string[]} activityIds
 * @param {CatalogServiceMilestone[]} [existingMilestones]
 * @returns {CatalogMilestoneActivityProgress[]}
 */
/**
 * @param {string[]} parameterKeys
 * @param {CatalogServiceMilestone[]} [existingMilestones]
 * @returns {CatalogMilestoneParameterUpdate[]}
 */
export function createCatalogParameterUpdateEntries(
  parameterKeys,
  existingMilestones = []
) {
  return parameterKeys
    .map((key) => key.trim())
    .filter(Boolean)
    .map((parameterKey) => {
      const prior = getLatestEoiParameterUpdate(
        existingMilestones,
        parameterKey
      );
      return {
        parameterKey,
        ...(prior?.value ? { value: prior.value } : {}),
        ...(prior?.unit ? { unit: prior.unit } : {}),
        ...(prior?.completed ? { completed: prior.completed } : {}),
      };
    });
}

/**
 * @param {CatalogServiceMilestone[]} milestones
 * @param {string} parameterKey
 * @returns {CatalogMilestoneParameterUpdate|null}
 */
export function getLatestEoiParameterUpdate(milestones, parameterKey) {
  let latest = null;

  for (const milestone of normalizeCatalogMilestonesList(milestones)) {
    const entry = (milestone.inputs.parameterUpdates ?? []).find(
      (row) => row.parameterKey === parameterKey
    );
    if (entry) {
      latest = entry;
    }
  }

  return latest;
}

/**
 * Final as-measured dimensions for service completion and delivery pack display.
 *
 * @param {CatalogServiceMilestone[]|unknown[]} milestones
 * @param {import("../serviceEoiDetails").ServiceEoiDetails|null|undefined} eoiDetails
 */
export function listServiceFinalParameterRows(milestones, eoiDetails) {
  return listFinalMilestoneParameterDefinitions(eoiDetails).map(
    (definition) => {
      const latest = getLatestEoiParameterUpdate(
        milestones,
        definition.parameterKey
      ) ?? {
        parameterKey: definition.parameterKey,
        unit: definition.defaultUnit,
      };

      return {
        ...definition,
        row: latest,
        complete: isEoiParameterUpdateComplete(latest),
        display: formatParameterValueDisplay(latest),
        matchesEoi: areEoiAndUpdatedValuesEqual(definition, latest),
      };
    }
  );
}

/**
 * Activity IDs whose latest progress is order-complete (done tick or 100%).
 *
 * @param {CatalogServiceMilestone[]|unknown[]|null|undefined} milestones
 * @returns {string[]}
 */
export function getCompletedCatalogActivityIds(milestones) {
  const sorted = normalizeCatalogMilestonesList(milestones);
  const activityIds = new Set();

  for (const milestone of sorted) {
    for (const row of milestone.inputs.activities) {
      if (row.activityId) {
        activityIds.add(row.activityId);
      }
    }
  }

  return [...activityIds].filter((activityId) => {
    const latest = getLatestCatalogActivityProgress(sorted, activityId);
    return latest && isCatalogActivityProgressComplete(latest);
  });
}

/**
 * Parameter keys recorded in a completed milestone.
 *
 * @param {CatalogServiceMilestone[]|unknown[]|null|undefined} milestones
 * @returns {string[]}
 */
export function getCompletedEoiParameterKeys(milestones) {
  const sorted = normalizeCatalogMilestonesList(milestones);
  const completed = new Set();

  for (const milestone of sorted) {
    if (
      resolveCatalogMilestoneDisplayStatus(milestone) !==
      MILESTONE_STATUS.COMPLETED
    ) {
      continue;
    }

    for (const row of milestone.inputs.parameterUpdates ?? []) {
      if (row.parameterKey && isEoiParameterUpdateComplete(row)) {
        completed.add(row.parameterKey);
      }
    }
  }

  return [...completed];
}

/**
 * Activity and parameter keys already assigned in milestone drafts.
 *
 * @param {CatalogServiceMilestone[]|unknown[]|null|undefined} milestones
 */
export function collectReservedCatalogSelectionIds(milestones) {
  const sorted = normalizeCatalogMilestonesList(milestones);
  /** @type {string[]} */
  const activityIds = [];
  /** @type {string[]} */
  const parameterKeys = [];

  for (const milestone of sorted) {
    for (const row of milestone.inputs.activities) {
      if (row.activityId) activityIds.push(row.activityId);
    }
    for (const row of milestone.inputs.parameterUpdates ?? []) {
      if (row.parameterKey) parameterKeys.push(row.parameterKey);
    }
  }

  return { activityIds, parameterKeys };
}

export function createCatalogActivityProgressEntries(
  activityIds,
  existingMilestones = []
) {
  return activityIds
    .map((activityId) => activityId.trim())
    .filter(Boolean)
    .map((activityId) => {
      const catalogActivity = getRemanufacturingActivity(activityId);
      const prior = getLatestCatalogActivityProgress(
        existingMilestones,
        activityId
      );

      if (
        catalogActivity?.progressType ===
        REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
      ) {
        const priorRecorded = getCatalogActivityRecordedPercent(
          prior ?? { activityId }
        );
        return {
          activityId,
          progressPercent: priorRecorded,
          ...(priorRecorded > 0
            ? { recordedProgressPercent: priorRecorded }
            : {}),
        };
      }

      return {
        activityId,
        completed: Boolean(prior?.completed),
      };
    });
}

/**
 * @param {string} categoryId
 */
export function formatCatalogMilestoneCategoryTitle(categoryId) {
  const category = getRemanufacturingActivityCategory(categoryId);
  if (!category) return "";

  return category.id
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * When the provider leaves the milestone title empty, prefer the shared TMCOMAS
 * activity category (e.g. "Inspection") over individual activity labels.
 *
 * @param {string} explicitTitle
 * @param {string[]} activityIds
 */
export function resolveCatalogMilestoneTitle(explicitTitle, activityIds) {
  const trimmed = explicitTitle.trim();
  if (trimmed) return trimmed;

  const categoryIds = [
    ...new Set(
      activityIds
        .map((id) => getRemanufacturingActivity(id)?.categoryId)
        .filter(Boolean)
    ),
  ];

  if (categoryIds.length === 1) {
    return formatCatalogMilestoneCategoryTitle(categoryIds[0]);
  }

  if (categoryIds.length > 1) {
    return categoryIds.map(formatCatalogMilestoneCategoryTitle).join(" · ");
  }

  return (
    activityIds
      .map((id) => getRemanufacturingActivity(id)?.label)
      .filter(Boolean)
      .slice(0, 2)
      .join(" · ") || ""
  );
}

/**
 * @param {string} title
 * @param {string[]} activityIds
 * @param {Array<{ key?: string }>} existingMilestones
 */
export function createCatalogMilestoneDraft(
  title,
  activityIds,
  existingMilestones = [],
  parameterKeys = []
) {
  const key = nextCatalogMilestoneKey(existingMilestones);
  const activities = createCatalogActivityProgressEntries(
    activityIds,
    existingMilestones
  );
  const parameterUpdates = createCatalogParameterUpdateEntries(
    parameterKeys,
    existingMilestones
  );

  const resolvedTitle = resolveCatalogMilestoneTitle(title, activityIds) || key;

  return normalizeCatalogMilestone({
    id: key.toLowerCase(),
    key,
    title: resolvedTitle,
    status: MILESTONE_STATUS.PENDING,
    sortOrder: existingMilestones.length + 1,
    inputs: {
      activities,
      ...(parameterUpdates.length > 0 ? { parameterUpdates } : {}),
      outcomeFiles: [],
    },
  });
}

/**
 * Locks a catalog plan by designating only its last milestone as the service
 * completion milestone.
 *
 * @param {CatalogServiceMilestone[]} milestones
 * @returns {CatalogServiceMilestone[]}
 */
export function finalizeCatalogMilestonePlan(milestones) {
  const sorted = normalizeCatalogMilestonesList(milestones);
  const terminalIndex = sorted.length - 1;

  return sorted.map((milestone, index) => {
    const { serviceCompletionConfirmed: _ignored, ...inputs } =
      milestone.inputs;
    return {
      ...milestone,
      inputs: {
        ...inputs,
        ...(index === terminalIndex
          ? { serviceCompletionConfirmed: true }
          : {}),
      },
    };
  });
}

/**
 * Order-level completion (100% or done tick) for display on finished work.
 *
 * @param {CatalogMilestoneActivityProgress} activityProgress
 */
export function isCatalogActivityProgressComplete(activityProgress) {
  const catalogActivity = getRemanufacturingActivity(
    activityProgress.activityId
  );
  if (!catalogActivity) return false;

  if (
    catalogActivity.progressType ===
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
  ) {
    return Number(activityProgress.progressPercent) >= 100;
  }

  return Boolean(activityProgress.completed);
}

/**
 * Whether an activity is satisfied for completing the **current milestone only**.
 * Percent activities must reach 100%. Partial progress can still be saved without
 * completing the milestone.
 *
 * @param {CatalogMilestoneActivityProgress} activityProgress
 */
export function isCatalogActivitySatisfiedForMilestoneCompletion(
  activityProgress
) {
  const catalogActivity = getRemanufacturingActivity(
    activityProgress.activityId
  );
  if (!catalogActivity) return false;

  if (
    catalogActivity.progressType ===
    REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
  ) {
    return getCatalogActivityRecordedPercent(activityProgress) === 100;
  }

  return Boolean(activityProgress.completed);
}

/**
 * @param {CatalogServiceMilestone[]} milestones
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function validateCatalogServicePlanForCompletion(milestones) {
  const sorted = normalizeCatalogMilestonesList(milestones);
  const activityIds = new Set();

  for (const milestone of sorted) {
    for (const activityProgress of milestone.inputs.activities) {
      if (activityProgress.activityId) {
        activityIds.add(activityProgress.activityId);
      }
    }
  }

  for (const activityId of activityIds) {
    const catalogActivity = getRemanufacturingActivity(activityId);
    const latest = getLatestCatalogActivityProgress(sorted, activityId);
    if (!catalogActivity || !latest) {
      return {
        ok: false,
        reason: "catalogValidationOrderActivitiesIncomplete",
      };
    }

    if (
      catalogActivity.progressType ===
      REMANUFACTURING_PROGRESS_TYPE.PERCENT_COMPLETION
    ) {
      if (Number(latest.progressPercent) < 100) {
        return { ok: false, reason: "catalogValidationOrderPercentIncomplete" };
      }
      continue;
    }

    if (!latest.completed) {
      return { ok: false, reason: "catalogValidationOrderDoneTickIncomplete" };
    }
  }

  return { ok: true };
}

/**
 * @param {CatalogServiceMilestone} milestone
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function validateFinalMilestoneParameterUpdates(milestone) {
  if (!milestone.inputs.serviceCompletionConfirmed) {
    return { ok: true };
  }

  const rows = milestone.inputs.parameterUpdates ?? [];
  for (const parameterKey of EOI_UPDATABLE_PARAMETER_KEYS) {
    const row = rows.find((entry) => entry.parameterKey === parameterKey);
    if (!row || !isEoiParameterUpdateComplete(row)) {
      return {
        ok: false,
        reason: "catalogValidationFinalParametersIncomplete",
      };
    }
  }

  return { ok: true };
}

export function validateCatalogMilestoneForCompletion(milestone) {
  if (!milestone.inputs.activities.length) {
    return { ok: false, reason: "catalogValidationActivitiesRequired" };
  }

  const finalParametersValidation =
    validateFinalMilestoneParameterUpdates(milestone);
  if (!finalParametersValidation.ok) {
    return finalParametersValidation;
  }

  for (const activityProgress of milestone.inputs.activities) {
    if (!isCatalogActivitySatisfiedForMilestoneCompletion(activityProgress)) {
      const catalogActivity = getRemanufacturingActivity(
        activityProgress.activityId
      );
      if (
        catalogActivity?.progressType ===
        REMANUFACTURING_PROGRESS_TYPE.DONE_TICK
      ) {
        return { ok: false, reason: "catalogValidationDoneTickIncomplete" };
      }
      return { ok: false, reason: "catalogValidationPercentIncomplete" };
    }
  }

  return { ok: true };
}

/**
 * @param {CatalogServiceMilestone[]} milestones
 */
export function isCatalogMilestonePlanComplete(milestones) {
  if (!milestones.length) return false;

  const sorted = normalizeCatalogMilestonesList(milestones);
  const allCompleted = sorted.every(
    (milestone) => milestone.status === MILESTONE_STATUS.COMPLETED
  );
  if (!allCompleted) return false;

  const terminal = sorted[sorted.length - 1];
  if (!terminal.inputs.serviceCompletionConfirmed) {
    return false;
  }

  return validateCatalogServicePlanForCompletion(sorted).ok;
}

/**
 * @param {CatalogServiceMilestone[]} milestones
 */
export function getActiveCatalogMilestone(milestones) {
  const sorted = [...milestones].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    sorted.find(
      (milestone) => milestone.status === MILESTONE_STATUS.IN_PROGRESS
    ) ??
    sorted.find((milestone) => milestone.status === MILESTONE_STATUS.PENDING) ??
    null
  );
}

/**
 * Ensures the first pending catalog milestone is in progress when a plan starts.
 *
 * @param {CatalogServiceMilestone[]} milestones
 * @returns {CatalogServiceMilestone[]}
 */
export function ensureCatalogMilestoneInProgress(milestones) {
  const sorted = normalizeCatalogMilestonesList(milestones);
  if (!sorted.length) {
    return sorted;
  }

  if (
    sorted.some(
      (milestone) => milestone.status === MILESTONE_STATUS.IN_PROGRESS
    )
  ) {
    return sorted;
  }

  const nextPending = sorted.find(
    (milestone) => milestone.status === MILESTONE_STATUS.PENDING
  );
  if (!nextPending) {
    return sorted;
  }

  return sorted.map((milestone) =>
    milestone.key === nextPending.key
      ? { ...milestone, status: MILESTONE_STATUS.IN_PROGRESS }
      : milestone
  );
}

/**
 * @param {CatalogServiceMilestone} milestone
 * @param {{ finishingService?: boolean, allMilestones?: CatalogServiceMilestone[] }} [options]
 * @returns {string|null}
 */
export function getCatalogMilestoneCompletionBlockerKey(
  milestone,
  options = {}
) {
  const validation = validateCatalogMilestoneForCompletion(milestone);
  if (!validation.ok) {
    return validation.reason;
  }

  if (
    options.finishingService &&
    !milestone.inputs.serviceCompletionConfirmed
  ) {
    return "catalogValidationServiceCompletionRequired";
  }

  if (options.finishingService && options.allMilestones?.length) {
    const orderValidation = validateCatalogServicePlanForCompletion(
      options.allMilestones
    );
    if (!orderValidation.ok) {
      return orderValidation.reason;
    }
  }

  return null;
}
